/**
 * AJAX Collection Filtering
 * Prevents page reloads when applying filters and provides smooth UX
 */

(function() {
  'use strict';

  const CollectionFilters = {
    selectors: {
      filterForm: '[data-faceted-filter-form]',
      productGrid: '.product-list',
      contentWrapper: '.has-sidebar-option',
      filterInputs: 'input[type="checkbox"], input[type="number"]',
      submitButtons: 'input[type="submit"]',
      activeFilters: '.faceted-active-filters',
      sidebarBlock: '.sidebar-block',
      sortSelect: '#sort-by',
      tagSelect: '#tag_filter',
      loadMoreButton: '.js-load-more a[data-load-more]',
      paginationLinks: '.pagination a',
      tagFilterInput: '[data-tag-filter-input]',
      tagFilterApply: '[data-tag-filter-apply]',
      tagFilterLabel: '[data-tag-filter-label]'
    },
    
    classes: {
      loading: 'collection-filtering--loading',
      fadeOut: 'collection-filtering--fade-out'
    },
    
    debug: false, // Set to true for debugging
    
    log: function(message, data) {
      if (this.debug) {
        console.log('[CollectionFilters]', message, data || '');
      }
    },
    
    initialized: false,
    
    init: function() {
      // Prevent duplicate initialization
      if (this.initialized && this.filterForm && this.productGrid) {
        this.log('Already initialized, skipping');
        return;
      }
      
      this.log('Initializing AJAX Collection Filters');
      
      this.filterForm = document.querySelector(this.selectors.filterForm);
      this.productGrid = document.querySelector(this.selectors.productGrid);
      this.contentWrapper = document.querySelector(this.selectors.contentWrapper);
      this.sortSelect = document.querySelector(this.selectors.sortSelect);
      this.tagSelect = document.querySelector(this.selectors.tagSelect);
      
      this.log('Filter form found:', !!this.filterForm);
      this.log('Product grid found:', !!this.productGrid);
      
      if (!this.productGrid) {
        this.log('No product grid found, AJAX filtering disabled');
        return;
      }
      
      this.bindEvents();
      this.bindTagFilterEvents();
      
      if (!this.historySetup) {
        this.setupHistoryState();
        this.historySetup = true;
      }
      
      this.initialized = true;
      this.log('AJAX Collection Filters initialized successfully');
    },
    
    bindEvents: function() {
      this.log('Binding events to filters');
      
      // Handle faceted filter form submissions
      if (this.filterForm) {
        this.log('Attaching events to filter form');
        
        // Mark form as handled
        this.filterForm.dataset.ajaxBound = 'true';
        
        // Prevent form's default action attribute from being followed
        this.filterForm.setAttribute('onsubmit', 'return false;');
        
        this.filterForm.addEventListener('submit', this.handleFilterSubmit.bind(this), true);
        
        // Auto-submit on checkbox change
        const checkboxes = this.filterForm.querySelectorAll('input[type="checkbox"]');
        this.log('Found checkboxes:', checkboxes.length);
        checkboxes.forEach(checkbox => {
          checkbox.addEventListener('change', this.handleCheckboxChange.bind(this), false);
        });
        
        // Handle number inputs (price filters)
        const numberInputs = this.filterForm.querySelectorAll('input[type="number"]');
        this.log('Found number inputs:', numberInputs.length);
        numberInputs.forEach(input => {
          input.addEventListener('blur', this.handleNumberInputChange.bind(this));
          input.addEventListener('input', (e) => {
            // Prevent form submission on enter key
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          });
        });
        
        // Intercept submit buttons - multiple layers
        const submitButtons = this.filterForm.querySelectorAll('input[type="submit"], button[type="submit"]');
        this.log('Found submit buttons:', submitButtons.length);
        submitButtons.forEach(button => {
          // Remove type="submit" to prevent browser from submitting
          button.setAttribute('type', 'button');
          button.dataset.wasSubmit = 'true';
          
          button.addEventListener('click', (e) => {
            this.log('Submit button clicked');
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.submitFilterForm(this.filterForm);
            return false;
          }, true);
        });
        
        // Handle clear filter links
        const clearLinks = this.filterForm.querySelectorAll('a[href*="filter"], a.faceted-filter-group-display__header-clear');
        this.log('Found clear links:', clearLinks.length);
        clearLinks.forEach(link => {
          link.addEventListener('click', this.handleFilterLinkClick.bind(this), true);
        });
      }
      
      // Handle active filter removal links (outside form)
      const activeFilterLinks = document.querySelectorAll('.faceted-active-filters__remove-filter, .faceted-active-filters__clear');
      this.log('Found active filter links:', activeFilterLinks.length);
      activeFilterLinks.forEach(link => {
        link.addEventListener('click', this.handleFilterLinkClick.bind(this), true);
      });
      
      // Handle sort dropdown
      if (this.sortSelect) {
        this.log('Attaching sort dropdown handler');
        this.sortSelect.removeEventListener('change', this.handleSortChange);
        this.sortSelect.addEventListener('change', this.handleSortChange.bind(this), false);
      }
      
      // Handle tag filter dropdown
      if (this.tagSelect) {
        this.log('Attaching tag dropdown handler');
        this.tagSelect.removeEventListener('change', this.handleTagChange);
        this.tagSelect.addEventListener('change', this.handleTagChange.bind(this), false);
      }
      
      // Handle pagination links
      this.bindPaginationLinks();
      
      // Global link interception for filter-related URLs
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (link && link.href && 
            (link.href.includes('filter.v.') || 
             link.classList.contains('faceted-active-filters__remove-filter') ||
             link.classList.contains('faceted-active-filters__clear') ||
             link.classList.contains('faceted-filter-group-display__header-clear'))) {
          this.log('Filter link intercepted:', link.href);
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.fetchFilteredResults(link.href);
          return false;
        }
      }, true);
    },
    
    handleNumberInputChange: function(event) {
      // Debounce number input changes
      clearTimeout(this.numberInputTimeout);
      this.numberInputTimeout = setTimeout(() => {
        if (this.filterForm) {
          this.submitFilterForm(this.filterForm);
        }
      }, 500);
    },
    
    bindPaginationLinks: function() {
      const paginationLinks = document.querySelectorAll(this.selectors.paginationLinks);
      paginationLinks.forEach(link => {
        link.addEventListener('click', this.handlePaginationClick.bind(this), true);
      });
    },
    
    handlePaginationClick: function(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const url = event.currentTarget.getAttribute('href');
      if (url) {
        this.fetchFilteredResults(url);
      }
      return false;
    },
    
    handleCheckboxChange: function(event) {
      const checkbox = event.target;
      this.log('Checkbox changed:', checkbox.name);
      // Don't prevent default on checkboxes - we want them to toggle
      // But we do prevent the form submission

      // Ensure product type filter behaves as single-select
      // Shopify uses the param name "filter.v.product_type" for product type filters
      if (
        this.filterForm &&
        checkbox.type === 'checkbox' &&
        checkbox.name === 'filter.v.product_type' &&
        checkbox.checked
      ) {
        const groupCheckboxes = this.filterForm.querySelectorAll(
          'input[type="checkbox"][name="' + checkbox.name + '"]'
        );
        groupCheckboxes.forEach(cb => {
          if (cb !== checkbox) {
            cb.checked = false;
          }
        });
      }
      
      // Add a small delay to prevent rapid-fire requests
      clearTimeout(this.checkboxTimeout);
      this.checkboxTimeout = setTimeout(() => {
        if (this.filterForm) {
          this.log('Submitting filter form after checkbox change');
          this.submitFilterForm(this.filterForm);
        }
      }, 300);
    },
    
    handleFilterSubmit: function(event) {
      this.log('Filter form submit intercepted');
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.submitFilterForm(event.target);
      return false;
    },
    
    handleFilterLinkClick: function(event) {
      this.log('Filter link clicked:', event.currentTarget.href);
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const url = event.currentTarget.getAttribute('href');
      if (url) {
        this.fetchFilteredResults(url);
      }
      return false;
    },
    
    handleSortChange: function(event) {
      this.log('Sort changed:', event.target.value);
      const sortValue = event.target.value;
      const url = new URL(window.location.href);
      
      if (sortValue && sortValue !== 'manual') {
        url.searchParams.set('sort_by', sortValue);
      } else {
        url.searchParams.delete('sort_by');
      }
      
      this.fetchFilteredResults(url.toString());
    },
    
    handleTagChange: function(event) {
      this.log('Tag changed:', event.target.value);
      const url = event.target.value;
      if (url) {
        this.fetchFilteredResults(url);
      }
    },
    
    submitFilterForm: function(form) {
      this.log('submitFilterForm called');
      const formData = new FormData(form);
      const url = new URL(window.location.href);
      
      // Preserve sort_by parameter
      const currentSortBy = url.searchParams.get('sort_by');
      
      // Clear existing filter params
      const keysToDelete = [];
      for (const key of url.searchParams.keys()) {
        if (key.startsWith('filter.')) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => url.searchParams.delete(key));
      
      // Add new filter params
      for (const [key, value] of formData.entries()) {
        if (!value) continue;
        
        // For faceted filters, allow multiple values per key
        // (e.g., multiple checkboxes in the same group)
        if (key.startsWith('filter.')) {
          url.searchParams.append(key, value);
        } else {
          url.searchParams.set(key, value);
        }
      }
      
      // Restore sort_by if it existed and wasn't in the form
      if (currentSortBy && !formData.has('sort_by')) {
        url.searchParams.set('sort_by', currentSortBy);
      }
      
      this.log('Fetching filtered results:', url.toString());
      this.fetchFilteredResults(url.toString());
    },
    
    fetchFilteredResults: function(url) {
      this.log('fetchFilteredResults called:', url);
      
      // Prevent rapid-fire requests
      if (this.isLoading) {
        this.log('Already loading, ignoring request');
        return;
      }
      
      this.isLoading = true;
      
      // Show loading state
      this.showLoadingState();
      
      // Fetch the filtered results
      fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => response.text())
      .then(html => {
        this.updatePageContent(html, url);
        
        // No automatic scrolling - user stays at their current position
        
        this.hideLoadingState();
        this.isLoading = false;
      })
      .catch(error => {
        console.error('Error fetching filtered results:', error);
        this.hideLoadingState();
        this.isLoading = false;
        
        // Try one more time before falling back to page reload
        if (!this.retrying) {
          this.retrying = true;
          setTimeout(() => {
            this.log('Retrying fetch after error');
            this.fetchFilteredResults(url);
            this.retrying = false;
          }, 1000);
        } else {
          // If retry fails, fallback to regular navigation
          console.log('AJAX filtering failed, falling back to page reload');
          window.location.href = url;
        }
      });
    },
    
    updatePageContent: function(html, url) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update product grid
      const newProductGrid = doc.querySelector(this.selectors.productGrid);
      if (newProductGrid && this.productGrid) {
        this.productGrid.innerHTML = newProductGrid.innerHTML;
        
        // Trigger lazy load for images if lazysizes is available
        if (window.lazySizes) {
          window.lazySizes.init();
        }
      }
      
      // Update sidebar filters if present
      const newSidebar = doc.querySelector('.sidebar');
      const currentSidebar = document.querySelector('.sidebar');
      if (newSidebar && currentSidebar) {
        currentSidebar.innerHTML = newSidebar.innerHTML;
      }
      
      // Update active filters display
      const newActiveFilters = doc.querySelector(this.selectors.activeFilters);
      const currentActiveFilters = document.querySelector(this.selectors.activeFilters);
      if (newActiveFilters && currentActiveFilters) {
        currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
      }
      
      // Update pagination if present
      const newPagination = doc.querySelector('.pagination');
      const currentPagination = document.querySelector('.pagination');
      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
      } else if (!newPagination && currentPagination) {
        currentPagination.remove();
      }
      
      // Update load more button if present
      const newLoadMore = doc.querySelector('.js-load-more');
      const currentLoadMore = document.querySelector('.js-load-more');
      if (newLoadMore && currentLoadMore) {
        currentLoadMore.outerHTML = newLoadMore.outerHTML;
      } else if (!newLoadMore && currentLoadMore) {
        currentLoadMore.remove();
      }
      
      // Update product count display if present
      const newProductCount = doc.querySelector('.collection-product-count');
      const currentProductCount = document.querySelector('.collection-product-count');
      if (newProductCount && currentProductCount) {
        currentProductCount.innerHTML = newProductCount.innerHTML;
      }
      
      // Update sort/tag dropdowns to maintain selected values
      const newSortSelect = doc.querySelector(this.selectors.sortSelect);
      const newTagSelect = doc.querySelector(this.selectors.tagSelect);
      
      if (newSortSelect && this.sortSelect) {
        this.sortSelect.value = newSortSelect.value;
      }
      if (newTagSelect && this.tagSelect) {
        this.tagSelect.value = newTagSelect.value;
      }
      
      // Update browser URL and history
      if (url !== window.location.href) {
        window.history.pushState({ url: url }, '', url);
      }
      
      // Reinitialize events after DOM update
      this.initialized = false; // Allow re-initialization
      setTimeout(() => {
        this.init();
        // Also re-run the form interceptor
        if (window.interceptFilterForms) {
          window.interceptFilterForms();
        }
      }, 100);
    },
    
    showLoadingState: function() {
      if (this.productGrid) {
        this.productGrid.classList.add(this.classes.loading);
        this.productGrid.style.opacity = '0.5';
        this.productGrid.style.pointerEvents = 'none';
      }
      
      // Add slight opacity to sidebar during filtering
      const sidebar = document.querySelector('.sidebar');
      if (sidebar) {
        sidebar.style.opacity = '0.7';
        sidebar.style.pointerEvents = 'none';
      }
      
      // Use existing load-more__icon for loading indicator
      const loadMoreIcon = document.querySelector('.load-more__icon');
      if (loadMoreIcon) {
        loadMoreIcon.classList.add('loading-in-progress');
        loadMoreIcon.style.cssText = 'height: 40px; width: 40px; opacity: 1;';
      }
    },
    
    hideLoadingState: function() {
      if (this.productGrid) {
        this.productGrid.classList.remove(this.classes.loading);
        this.productGrid.style.opacity = '1';
        this.productGrid.style.pointerEvents = 'auto';
      }
      
      // Restore sidebar opacity
      const sidebar = document.querySelector('.sidebar');
      if (sidebar) {
        sidebar.style.opacity = '1';
        sidebar.style.pointerEvents = 'auto';
      }
      
      // Hide existing load-more__icon loading indicator
      const loadMoreIcon = document.querySelector('.load-more__icon');
      if (loadMoreIcon) {
        loadMoreIcon.classList.remove('loading-in-progress');
        loadMoreIcon.style.cssText = 'height: 0; width: 0; opacity: 0;';
      }
    },
    
    setupHistoryState: function() {
      // Handle browser back/forward buttons
      window.addEventListener('popstate', (event) => {
        if (event.state && event.state.url) {
          this.fetchFilteredResults(event.state.url);
        } else {
          this.fetchFilteredResults(window.location.href);
        }
      });
      
      // Store initial state
      if (!window.history.state) {
        window.history.replaceState({ url: window.location.href }, '');
      }
    },
    
    bindTagFilterEvents: function() {
      this.log('Binding tag filter events');
      
      // Use event delegation on document to catch all clicks
      if (!this.tagFilterHandlerBound) {
        this.log('Setting up tag filter event delegation');
        
        // Handle checkbox changes - auto-apply when clicking checkboxes
        document.addEventListener('click', (event) => {
          const tagCheckbox = event.target.closest('[data-tag-filter-input]');
          if (tagCheckbox && tagCheckbox.type === 'checkbox') {
            this.log('Tag checkbox clicked, auto-applying filter');
            
            // Delay slightly to allow checkbox state to update
            setTimeout(() => {
              this.handleTagFilterApply(event);
            }, 100);
          }
        }, true);
        
        this.tagFilterHandlerBound = true;
        this.log('Tag filter event delegation set up');
      }
      
      const tagInputs = document.querySelectorAll('[data-tag-filter-input]');
      this.log('Found tag filter inputs:', tagInputs.length);
    },
    
    handleTagFilterApply: function(event) {
      this.log('Applying tag filters');
      
      try {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        
        // Get all checked tag filter inputs
        const checkedInputs = document.querySelectorAll('[data-tag-filter-input]:checked');
        const selectedTags = Array.from(checkedInputs).map(input => input.value);
        
        this.log('Selected tags:', selectedTags);
        
        // Build URL with tags
        const url = new URL(window.location.href);
        const pathParts = url.pathname.split('/').filter(part => part);
        
        // Build base URL (e.g., /collections/collection-handle)
        let baseUrl = url.origin;
        if (pathParts.length >= 2 && pathParts[0] === 'collections') {
          baseUrl += '/collections/' + pathParts[1];
        } else {
          baseUrl += url.pathname.split('?')[0].replace(/\/[^\/]*$/, '');
        }
        
        // Add tags to URL
        let newUrl = baseUrl;
        if (selectedTags.length > 0) {
          newUrl += '/' + selectedTags.join('+');
        }
        
        // Preserve ALL query params (like sort_by and any faceted filter params)
        const queryParams = new URLSearchParams();
        
        // Copy all existing query params
        for (const [key, value] of url.searchParams) {
          queryParams.set(key, value);
        }
        
        // Add query string if there are any params
        const queryString = queryParams.toString();
        if (queryString) {
          newUrl += '?' + queryString;
        }
        
        this.log('Navigating to:', newUrl);
        this.fetchFilteredResults(newUrl);
      } catch (error) {
        console.error('Error applying tag filters:', error);
      }
    }
  };
  
  // Set up multiple layers of form submission interceptors
  // This ensures we catch form submissions no matter what
  
  // Layer 1: Capture phase (runs first, before anything else)
  document.addEventListener('submit', function(e) {
    if (e.target && e.target.matches && e.target.matches('[data-faceted-filter-form]')) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // If CollectionFilters is initialized, use it
      if (CollectionFilters.filterForm) {
        CollectionFilters.submitFilterForm(e.target);
      }
      return false;
    }
  }, true);
  
  // Layer 2: Bubble phase (backup)
  document.addEventListener('submit', function(e) {
    if (e.target && e.target.matches && e.target.matches('[data-faceted-filter-form]')) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }
  }, false);
  
  // Layer 3: Override form submission directly
  function interceptFilterForms() {
    const filterForms = document.querySelectorAll('[data-faceted-filter-form]');
    filterForms.forEach(function(form) {
      if (!form.dataset.ajaxIntercepted) {
        form.dataset.ajaxIntercepted = 'true';
        
        // Override the submit method
        const originalSubmit = form.submit;
        form.submit = function() {
          if (CollectionFilters.filterForm) {
            CollectionFilters.submitFilterForm(form);
          }
          return false;
        };
        
        // Add event listener
        form.addEventListener('submit', function(e) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          if (CollectionFilters.filterForm) {
            CollectionFilters.submitFilterForm(form);
          }
          return false;
        }, true);
      }
    });
  }
  
  // Expose interceptFilterForms globally
  window.interceptFilterForms = interceptFilterForms;
  
  // Run immediately and on DOM changes
  interceptFilterForms();
  if (window.MutationObserver) {
    const observer = new MutationObserver(function() {
      interceptFilterForms();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
  
  // Initialize on DOM ready - multiple attempts to ensure it runs
  function safeInit() {
    try {
      CollectionFilters.init();
      interceptFilterForms();
    } catch(e) {
      console.error('CollectionFilters initialization error:', e);
    }
  }
  
  // Immediate initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }
  
  // Backup initialization after a short delay (in case DOM isn't fully ready)
  setTimeout(function() {
    safeInit();
    interceptFilterForms();
  }, 500);
  
  // Another backup after page is fully loaded
  window.addEventListener('load', function() {
    safeInit();
    interceptFilterForms();
  });
  
  // Expose for external use if needed
  window.CollectionFilters = CollectionFilters;
  
  // Allow manual reinitialization
  window.reinitCollectionFilters = function() {
    safeInit();
    interceptFilterForms();
  };
})();

